import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    Box,
    Button,
    MenuItem,
    Stack,
    TextField,
    Typography,
    IconButton,
    Checkbox,
    ListItemText,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import { DataGrid } from "@mui/x-data-grid";
import { GlobalState } from "context/GlobalContext";
import { pythonVmInstance } from "utils/AxiosInstances/RareboxPythonInstance";
import { AuthState } from "context/AuthContext";
import { roleAccess } from "../DiscountMasterList";
import { Discount_Master_store_Brands, DISOUNT_MASTER_CHANNEL_OPTIONS } from "../data";
import FileSaver from "file-saver";
import FileDownloadIcon from '@mui/icons-material/FileDownload';

const initialFormData = {
    channel: "",
    brand: [],
};

const compactSelectMenuProps = {
    PaperProps: {
        sx: {
            mt: 0.5,
            borderRadius: 1.5,
            boxShadow: "0 8px 24px rgba(16,24,40,0.12)",
            border: "1px solid #e4e7ec",
            "& .MuiMenuItem-root": {
                minHeight: 32,
                fontSize: "11px",
                py: 0.5,
            },
            "& .MuiListItemText-primary": {
                fontSize: "11px",
            },
        },
    },
};

const STORE_FIELD_CONFIGS = {
    sitecode: {
        field: "sitecode",
        headerName: "Site Code",
        aliases: ["sitecode", "SITECODE", "site_code", "siteCode"],
        minWidth: 120,
    },
    storeCode: {
        field: "storeCode",
        headerName: "Store Code",
        aliases: ["store_code", "STORE_CODE", "storeCode"],
        minWidth: 120,
    },
    storeName: {
        field: "storeName",
        headerName: "Store Name",
        aliases: ["store_name", "storeName", "ginesys_short_name", "site_name", "store"],
        minWidth: 200,
        flex: 1,
        editableChannels: ["EBO", "MBO", "LFS"],
    },
    partnerName: {
        field: "partnerName",
        headerName: "Partner Name",
        aliases: ["partner","partner_site_name", "partner_name", "partnerName"],
        minWidth: 200,
        flex: 1,
    },
    channel: {
        field: "channel",
        headerName: "Channel",
        aliases: ["channel", "channel_name", "channelName"],
        minWidth: 100,
    },
    brand: {
        field: "brand",
        headerName: "Brand",
        aliases: ["brand", "brand_name", "brandName"],
        minWidth: 140,
    },
    zone: {
        field: "zone",
        headerName: "Zone",
        aliases: ["region", "zone", "zone_name", "zoneName"],
        minWidth: 100,
    },
    state: {
        field: "state",
        headerName: "State",
        aliases: ["state", "state_name", "stateName"],
        minWidth: 140,
    },
    city: {
        field: "city",
        headerName: "City",
        aliases: ["city", "city_name", "cityName"],
        minWidth: 140,
    },
    grade: {
        field: "grade",
        headerName: "Store Grade",
        aliases: ["grade", "store_grade", "storeGrade"],
        minWidth: 120,
        editableChannels: ["EBO", "MBO", "LFS"],
    },
    status: {
        field: "status",
        headerName: "Store Status",
        aliases: ["status", "store_status", "storeStatus"],
        minWidth: 120,
        editableChannels: ["EBO", "MBO", "LFS"],
    },
    contactNo: {
        field: "contactNo",
        headerName: "Contact Number",
        aliases: ["contact_no", "contactNo", "contact_number", "contactNumber", "phone", "mobile"],
        minWidth: 170,
        inputType: "contact",
        editableChannels: ["EBO", "MBO", "LFS"],
    },
    storeEmail: {
        field: "storeEmail",
        headerName: "Store Email",
        aliases: ["email", "store_email", "storeEmail", "contact1_email"],
        minWidth: 220,
        flex: 1,
        inputType: "email",
        editableChannels: ["EBO", "LFS"],
    },
    partnerEmail: {
        field: "partnerEmail",
        headerName: "Partner Email",
        aliases: ["partner_email", "partnerEmail", "contact2_email"],
        minWidth: 220,
        flex: 1,
        inputType: "email",
        editableChannels: ["MBO", "LFS"],
    },
    nsmEmail: {
        field: "nsmEmail",
        headerName: "NSM Email",
        aliases: ["nsm_email", "nsmEmail", "contact3_email"],
        minWidth: 220,
        flex: 1,
        inputType: "email",
        editableChannels: ["MBO", "LFS"],
    },
    zmEmail: {
        field: "zmEmail",
        headerName: "ZM Email",
        aliases: ["zm_email", "zmEmail", "contact4_email"],
        minWidth: 220,
        flex: 1,
        inputType: "email",
        editableChannels: ["MBO", "LFS"],
    },
    rmAsmEmail: {
        field: "rmAsmEmail",
        headerName: "RM/ASM Email",
        aliases: ["rm_asm_email", "rmAsmEmail", "rm_email", "asm_email", "contact5_email"],
        minWidth: 220,
        flex: 1,
        inputType: "email",
        editableChannels: ["MBO", "LFS"],
    },
};

const OPTIONAL_ID_COLUMNS = [
    { key: "sitecode", name: "Site Code" },
    { key: "storeCode", name: "Store Code" },
];

const hasColumnInRows = (rows = [], aliases = []) =>
    rows.some((row) => aliases.some((alias) => Object.prototype.hasOwnProperty.call(row || {}, alias)));

const STORE_COLUMNS_BY_CHANNEL = {
    EBO: [
        { key: "storeName", name: "Store Name", editable: true },
        { key: "channel", name: "Channel" },
        { key: "brand", name: "Brand" },
        { key: "zone", name: "Zone" },
        { key: "state", name: "State" },
        { key: "city", name: "City" },
        { key: "grade", name: "Store Grade", editable: true },
        { key: "status", name: "Store Status", editable: true },
        { key: "contactNo", name: "Contact Number", editable: true },
        { key: "storeEmail", name: "Store Email", editable: true },
    ],
    MBO: [
        { key: "storeName", name: "Store Name", editable: true },
        { key: "partnerName", name: "Partner Name" },
        { key: "channel", name: "Channel" },
        { key: "brand", name: "Brand" },
        { key: "zone", name: "Zone" },
        { key: "state", name: "State" },
        { key: "city", name: "City" },
        { key: "grade", name: "Store Grade", editable: true },
        { key: "status", name: "Store Status", editable: true },
        { key: "contactNo", name: "Contact Number", editable: true },
        { key: "partnerEmail", name: "Partner Email", editable: true },
        { key: "nsmEmail", name: "NSM Email", editable: true },
        { key: "zmEmail", name: "ZM Email", editable: true },
        { key: "rmAsmEmail", name: "RM/ASM Email", editable: true },
    ],
    LFS: [
        { key: "storeName", name: "Store Name", editable: true },
        { key: "partnerName", name: "Partner Name" },
        { key: "channel", name: "Channel" },
        { key: "brand", name: "Brand" },
        { key: "zone", name: "Zone" },
        { key: "state", name: "State" },
        { key: "city", name: "City" },
        { key: "grade", name: "Store Grade", editable: true },
        { key: "status", name: "Store Status" },
        { key: "contactNo", name: "Contact Number", editable: true },
        { key: "storeEmail", name: "Contact1 Email", editable: true },
        { key: "partnerEmail", name: "Contact2 Email", editable: true },
        { key: "nsmEmail", name: "Contact3 Email", editable: true },
        { key: "zmEmail", name: "Contact4 Email", editable: true },
        { key: "rmAsmEmail", name: "Contact5 Email", editable: true },
    ],
};

const DEFAULT_STORE_COLUMNS = STORE_COLUMNS_BY_CHANNEL.EBO;
const ROW_ID_ALIASES = ["sitecode", "SITECODE", "store_code", "STORE_CODE", "storeCode", "code", "site_code", "siteCode", "id"];

const getValueByAliases = (row, aliases = []) => {
    const matchingKey = aliases.find((key) => row?.[key] !== undefined && row?.[key] !== null && row?.[key] !== "");
    return matchingKey ? row?.[matchingKey] : "";
};

const setValueByAliases = (row, aliases = [], value) => {
    const existingKeys = aliases.filter((key) => row?.[key] !== undefined);
    const keysToUpdate = existingKeys.length ? existingKeys : aliases.slice(0, 1);

    return keysToUpdate.reduce(
        (acc, key) => ({
            ...acc,
            [key]: value,
        }),
        row
    );
};

const getStoreFieldValue = (row, fieldConfig) => getValueByAliases(row, fieldConfig?.aliases);
const isColumnEditable = (columnConfig) => Boolean(columnConfig?.editable);
const isValidEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
const getEmailList = (value) =>
    String(value || "")
        .split(",")
        .map((email) => email.trim())
        .filter(Boolean);
const normalizeEmailList = (value) => getEmailList(value).join(", ");
const isValidEmailList = (value) => {
    const emails = getEmailList(value);

    return emails.length === 0 || emails.every(isValidEmail);
};
const formatStoreCellValue = (value, fieldConfig) => {
    if (fieldConfig?.inputType === "email") {
        return normalizeEmailList(value);
    }

    return value;
};
const handleEditableInputKeyDown = (event) => {
    if (["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)) {
        event.stopPropagation();
    }
};

const editableCellInputSx = {
    width: "100%",
    "& .MuiInputBase-root": {
        height: 28,
        fontSize: "11px",
    },
    "& .MuiInputBase-input": {
        py: 0,
    },
    "& .MuiFormHelperText-root": {
        display: "none",
    },
};

const normalizeStoreColumnConfig = (columnConfig, channel) => {
    const columnKey = typeof columnConfig === "string" ? columnConfig : columnConfig?.key;
    const overrides = typeof columnConfig === "string" ? {} : columnConfig;
    const baseConfig = STORE_FIELD_CONFIGS[columnKey] || {};

    return {
        ...baseConfig,
        ...overrides,
        key: columnKey,
        field: overrides.field || baseConfig.field || columnKey,
        headerName: overrides.name || overrides.headerName || baseConfig.headerName || columnKey,
        aliases: overrides.aliases || baseConfig.aliases || [columnKey],
        editable: Boolean(overrides.editable),
    };
};

const createStoreDetailsColumns = ({
    columnConfigs,
    filteredRows,
    editingRowId,
    editValues,
    editErrors,
    onEditValueChange,
    onEditStart,
    onEditSave,
    onEditCancel,
    isReadOnly,
}) =>
    [
        {
            field: "sNo",
            headerName: "S.No",
            width: 70,
            sortable: false,
            filterable: false,
            renderCell: (params) =>
                filteredRows.findIndex((row) => row?._rowId === params.row?._rowId) + 1,
        },
        ...columnConfigs.map((fieldConfig) => {
            const editable = isColumnEditable(fieldConfig);
            const getSortableValue = (row) =>
                formatStoreCellValue(getStoreFieldValue(row, fieldConfig), fieldConfig) || "";

            return {
                field: fieldConfig.field,
                headerName: fieldConfig.headerName,
                minWidth: fieldConfig.minWidth,
                flex: fieldConfig.flex,
                // Row data uses BQ aliases (store_name, sitecode, etc.); map for sort/filter
                valueGetter: (_value, row) => getSortableValue(row),
                sortComparator: (a, b) =>
                    String(a || "").localeCompare(String(b || ""), undefined, {
                        numeric: true,
                        sensitivity: "base",
                    }),
                renderCell: (params) =>
                    editingRowId === params.row?._rowId && editable ? (
                        <Box sx={{ width: "100%", height: "100%", display: "flex", alignItems: "center" }}>
                            <TextField
                                size="small"
                                value={editValues[fieldConfig.key] || ""}
                                onChange={onEditValueChange(fieldConfig.key)}
                                onKeyDown={handleEditableInputKeyDown}
                                placeholder={fieldConfig.headerName}
                                error={Boolean(editErrors[fieldConfig.key])}
                                helperText={editErrors[fieldConfig.key]}
                                title={editErrors[fieldConfig.key] || ""}
                                inputProps={
                                    fieldConfig.inputType === "contact"
                                        ? { inputMode: "numeric", pattern: "[0-9]*" }
                                        : undefined
                                }
                                sx={editableCellInputSx}
                            />
                        </Box>
                    ) : (() => {
                        const displayValue = getSortableValue(params.row);

                        return displayValue ? (
                            <Typography
                                component="span"
                                title={displayValue}
                                sx={{
                                    width: "100%",
                                    fontSize: "10px",
                                    lineHeight: 1.35,
                                    whiteSpace: fieldConfig.inputType === "email" ? "normal" : "nowrap",
                                    overflow: "hidden",
                                    overflowWrap: fieldConfig.inputType === "email" ? "anywhere" : "normal",
                                    textOverflow: fieldConfig.inputType === "email" ? "clip" : "ellipsis",
                                }}
                            >
                                {displayValue}
                            </Typography>
                        ) : (
                            "-"
                        );
                    })(),
            };
        }),
        {
            field: "action",
            headerName: "Action",
            width: 170,
            sortable: false,
            filterable: false,
            renderCell: (params) =>
                editingRowId === params.row?._rowId ? (
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ height: "100%" }}>
                        <Button
                            size="small"
                            variant="contained"
                            onClick={onEditSave}
                            disabled={isReadOnly}
                            sx={{ height: 26, minWidth: 50, px: 1, fontSize: "10px", textTransform: "none" }}
                        >
                            Save
                        </Button>
                        <Button
                            size="small"
                            variant="outlined"
                            onClick={onEditCancel}
                            sx={{ height: 26, minWidth: 56, px: 1, fontSize: "10px", textTransform: "none" }}
                        >
                            Cancel
                        </Button>
                    </Stack>
                ) : (
                    <IconButton
                        size="small"
                        onClick={() => onEditStart(params.row)}
                        disabled={isReadOnly}
                        sx={{ width: 24, height: 24 }}
                    >
                        <EditIcon fontSize="small" />
                    </IconButton>
                ),
        },
    ].filter(Boolean);

function DiscountStoreMaster() {
    const [formData, setFormData] = useState(initialFormData);
    const [storeRows, setStoreRows] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [file, setFile] = useState("");
    const [editingRowId, setEditingRowId] = useState(null);
    const [editValues, setEditValues] = useState({});
    const [editErrors, setEditErrors] = useState({});
    const [paginationModel, setPaginationModel] = useState({
        page: 0,
        pageSize: 10,
    });

    const { user } = AuthState();
    const roleData = roleAccess(user);

    const role = ["VIEWER"].includes(roleData.roles);
    const { setSnackBar, snackBar, setSnackBarOpen, setLoadingOpen } = GlobalState();
    const activeChannel = formData.channel || "EBO";
    const activeColumnConfigs = useMemo(() => {
        const baseColumns = (STORE_COLUMNS_BY_CHANNEL[activeChannel] || DEFAULT_STORE_COLUMNS).map((columnConfig) =>
            normalizeStoreColumnConfig(columnConfig, activeChannel)
        );
        const optionalIdColumns = OPTIONAL_ID_COLUMNS.filter((columnConfig) => {
            const fieldConfig = STORE_FIELD_CONFIGS[columnConfig.key];
            return hasColumnInRows(storeRows, fieldConfig?.aliases || [columnConfig.key]);
        }).map((columnConfig) => normalizeStoreColumnConfig(columnConfig, activeChannel));

        return [...optionalIdColumns, ...baseColumns];
    }, [activeChannel, storeRows]);
    const editableColumnConfigs = useMemo(
        () => activeColumnConfigs.filter(isColumnEditable),
        [activeColumnConfigs]
    );
    const getActiveColumnConfig = useCallback(
        (fieldKey) => activeColumnConfigs.find((columnConfig) => columnConfig.key === fieldKey),
        [activeColumnConfigs]
    );

    const channelOptions = useMemo(
        () =>
            DISOUNT_MASTER_CHANNEL_OPTIONS.filter((item) =>
                roleData?.channelList?.includes(item.key)
            ),
        [roleData]
    );

    const brandOptions = useMemo(
        () =>
            Discount_Master_store_Brands.filter((item) =>
                roleData?.brandList?.includes(item.key)
            ),
        [roleData]
    );
    const selectedBrandParam = useMemo(() => (formData?.brand || []).join(","), [formData?.brand]);
    const hasSelectedChannelAndBrand = Boolean(formData?.channel) && (formData?.brand || []).length > 0;

    const filteredRows = useMemo(() => {
        const query = searchTerm.trim().toLowerCase();
        if (!query) return storeRows;

        return storeRows.filter((row) => {
            const valuesToSearch = activeColumnConfigs.map((columnConfig) => getStoreFieldValue(row, columnConfig));

            return valuesToSearch.some((value) =>
                String(value || "").toLowerCase().includes(query)
            );
        });
    }, [activeColumnConfigs, storeRows, searchTerm]);

    const handleEditStart = (row) => {
        setEditingRowId(row?._rowId);
        setEditValues(
            editableColumnConfigs.reduce(
                (acc, columnConfig) => ({
                    ...acc,
                    [columnConfig.key]: getStoreFieldValue(row, columnConfig),
                }),
                {}
            )
        );
        setEditErrors({});
    };

    const handleEditCancel = () => {
        setEditingRowId(null);
        setEditValues({});
        setEditErrors({});
    };

    const handleEditValueChange = (fieldKey) => (e) => {
        const fieldConfig = getActiveColumnConfig(fieldKey);
        const value =
            fieldConfig?.inputType === "contact"
                ? e.target.value.replace(/\D/g, "")
                : e.target.value;

        setEditValues((prev) => ({
            ...prev,
            [fieldKey]: value,
        }));

        if (fieldConfig?.inputType === "email") {
            const trimmedValue = value.trim();
            setEditErrors((prev) => ({
                ...prev,
                [fieldKey]: trimmedValue && !isValidEmailList(trimmedValue) ? "Enter valid email(s) separated by comma" : "",
            }));
            return;
        }

        setEditErrors((prev) => ({
            ...prev,
            [fieldKey]: "",
        }));
    };

    const handleGetStoreData = useCallback(async () => {
        try {
            if (!formData?.channel) {
                 setSnackBarOpen(true);
                setSnackBar({
                    message:"Please select the channel and try again",
                    severity: "error",
                    anchorOrigin: { horizontal: "center", vertical: "top" },
                });
                return;
            }
            if (!(formData?.brand || []).length) {
                 setSnackBarOpen(true);
                setSnackBar({
                    message:"Please select the brand and try again",
                    severity: "error",
                    anchorOrigin: { horizontal: "center", vertical: "top" },
                });
                return;
            }
            setLoadingOpen(true);
            const params = new URLSearchParams({
                channel: formData.channel,
                brand: selectedBrandParam,
            });
            const res = await pythonVmInstance.get(`/discount-master/discount-store-master?${params.toString()}`);
            const normalizedRows = (res?.data || []).map((item, index) => ({
                ...item,
                _rowId: getValueByAliases(item, ROW_ID_ALIASES) || `row-${index}`,
            }));
            setStoreRows(normalizedRows);
            setPaginationModel((prev) => ({ ...prev, page: 0 }));
            setLoadingOpen(false);
        } catch (err) {
            const message = err?.response?.data?.message;
            setSnackBarOpen(true);
            setSnackBar({
                message: message || "Failed to load store master records",
                severity: "error",
                anchorOrigin: { horizontal: "center", vertical: "top" },
            });
            setLoadingOpen(false);
        }
    }, [formData?.brand, formData?.channel, selectedBrandParam, setLoadingOpen, setSnackBar, setSnackBarOpen]);

    const handleStoreDetailsUpdate = async (updatedRow) => {
        try {
            const payload = {
                ...updatedRow,
                // store_code: getValueByAliases(updatedRow, ROW_ID_ALIASES),
                // ginesys_short_name: getStoreFieldValue(updatedRow, STORE_FIELD_CONFIGS.storeName),
                channel: (getStoreFieldValue(updatedRow, STORE_FIELD_CONFIGS.channel) || formData?.channel || "").trim(),
                brand: getStoreFieldValue(updatedRow, STORE_FIELD_CONFIGS.brand) || formData?.brand?.[0] || "",
                updated_by: user.email,
            };

            delete payload._rowId;

            // setDownloading(true);
            setLoadingOpen(true);
            await pythonVmInstance({
                url: `/discount-master/update-store-master`,data: payload,
                method: "PUT",
            })
            setSnackBarOpen(true);
            setSnackBar({
                ...snackBar,
                message: "Store master updated successfully",
                severity: "success",
                anchorOrigin: { horizontal: "center", vertical: "top" },
            });
            handleGetStoreData();
            setLoadingOpen(false);
        } catch (error) {
            setSnackBarOpen(true);
            setLoadingOpen(false);
            setSnackBar({
                ...snackBar,
                message: error?.response?.data?.message || "Failed to update store master",
                severity: "error",
                anchorOrigin: { horizontal: "center", vertical: "top" },
            });
        }
    }

    const handleEditSave = () => {
        const rowToUpdate = storeRows.find((item) => item?._rowId === editingRowId);
        if (!rowToUpdate) return;

        const nextErrors = editableColumnConfigs.reduce((acc, fieldConfig) => {
            if (fieldConfig?.inputType !== "email") return acc;

            const value = (editValues[fieldConfig.key] || "").trim();
            return {
                ...acc,
                [fieldConfig.key]: value && !isValidEmailList(value) ? "Enter valid email(s) separated by comma" : "",
            };
        }, {});

        if (Object.values(nextErrors).some(Boolean)) {
            setEditErrors(nextErrors);
            return;
        }

        const updatedRow = editableColumnConfigs.reduce((acc, fieldConfig) => {
            const value =
                fieldConfig?.inputType === "email"
                    ? normalizeEmailList(editValues[fieldConfig.key])
                    : editValues[fieldConfig.key] || "";

            return setValueByAliases(acc, fieldConfig.aliases, value);
        }, rowToUpdate);

        const updatedRowWithEmail = {
            ...updatedRow,
        };

        setStoreRows((prev) =>
            prev.map((item) =>
                item?._rowId === editingRowId
                    ? updatedRowWithEmail
                    : item
            )
        );
        handleStoreDetailsUpdate(updatedRowWithEmail);
        handleEditCancel();
    };

    const storeDetailsColumns = createStoreDetailsColumns({
        columnConfigs: activeColumnConfigs,
        filteredRows,
        editingRowId,
        editValues,
        editErrors,
        onEditValueChange: handleEditValueChange,
        onEditStart: handleEditStart,
        onEditSave: handleEditSave,
        onEditCancel: handleEditCancel,
        isReadOnly: role,
    });
    

    const handleFileChange = (e) => {
        const newFile = e.target.files[0];
        if (!newFile) return;

        const modifiedFile = new File([newFile], newFile.name, {
            type: newFile.type,
        });

        setFile(modifiedFile);
    };

    const handleChange = (field) => (e) => {
        setStoreRows([]);
        setSearchTerm("");
        handleEditCancel();
        setPaginationModel((prev) => ({ ...prev, page: 0 }));
        setFormData((prev) => ({
            ...prev,
            [field]: e.target.value,
            ...(field === "channel" ? { brand: [] } : {}),
        }));
    };

    const handleDownloadSample = async (type) => {
        try {
            if (!formData?.channel || !(formData?.brand || []).length) {
                setSnackBarOpen(true);
                setSnackBar({
                    ...snackBar,
                    message: "Please select channel and brand before downloading store master",
                    severity: "warning",
                    anchorOrigin: { horizontal: "center", vertical: "top" },
                });
                return;
            }
            // setDownloading(true);
            setLoadingOpen(true);
            const params = new URLSearchParams({
                channel: formData.channel,
                brand: selectedBrandParam,
                download_type: type,
            });
            await pythonVmInstance({
                url: `/discount-master/download-store-master?${params.toString()}`,
                headers: {
                    "Content-Type": "application/json",
                },
                method: "GET",
                responseType: "blob",
            }).then((res) => {
                const url = URL.createObjectURL(res.data);
                FileSaver.saveAs(url, `${formData?.channel}_${selectedBrandParam || "brands"}_store_master_sample.csv`);
                setLoadingOpen(false);
            });
        } catch (error) {
            setSnackBarOpen(true);
            setLoadingOpen(false);
            setSnackBar({
                ...snackBar,
                message:
                    error?.response?.data?.message ||
                    (type === "sample"
                        ? "Failed to download store master sample file"
                        : "Failed to download store master file"),
                severity: "error",
                anchorOrigin: { horizontal: "center", vertical: "top" },
            });
        }
    };

    const handleFileUpload = async (e) => {
        e.preventDefault();
        setLoadingOpen(true);
        let form = new FormData();
        if (!formData?.channel || !(formData?.brand || []).length) {
            setSnackBarOpen(true);
            setSnackBar({
                ...snackBar,
                message: "Please select channel and brand before uploading store master",
                severity: "warning",
                anchorOrigin: { horizontal: "center", vertical: "top" },
            });
            setLoadingOpen(false);
            return;
        }
        if (file === "") {
            // setLoadingOpen(false);
            setSnackBarOpen(true);
            setSnackBar({
                ...snackBar,
                message: "Please upload a store master file and try again",
                severity: "warning",
                anchorOrigin: { horizontal: "center", vertical: "top" },
            });
            setLoadingOpen(false);
            return;
        }

        form.append("files", file);
        form.append("email", user.email);
        form.append("channel", formData?.channel);
        form.append("brand", selectedBrandParam);
        pythonVmInstance
            .post("/discount-master/upload-store-master", form, { headers: { "Content-Type": "multipart/form-data" } })
            .then((res) => {
                setLoadingOpen(false);
                setSnackBarOpen(true);
                setSnackBar({
                    ...snackBar,
                    message: "Store master file uploaded successfully",
                    severity: "success",
                    anchorOrigin: { horizontal: "center", vertical: "top" },
                });

                document.getElementById("fileInput").value = "";
                setFile("")
                handleGetStoreData()
            })
            .catch((err) => {
                console.log("uploaded", err);
                const message = err?.response?.data?.message;
                setLoadingOpen(false);
                setSnackBarOpen(true);
                setSnackBar({
                    ...snackBar,
                    message: message ? message : "Failed to upload store master file",
                    severity: "error",
                    anchorOrigin: { horizontal: "center", vertical: "top" },
                });
            });
    };

    // useEffect(() => {
    //     if (formData?.channel && (formData?.brand || []).length){
    //         handleGetStoreData();
    //     }
    // }, [formData?.brand, formData?.channel, handleGetStoreData]);

    useEffect(() => {
        setPaginationModel((prev) => ({ ...prev, page: 0 }));
    }, [searchTerm, activeChannel]);


    console.log("dsfghjopkhgf", storeRows)

    return (
        <Stack width="100%" p={1.5} spacing={1.25}>
            <Box display="flex" columnGap={1.25} rowGap={1} mb="8px" flexWrap="wrap">
                <Box display="flex" columnGap={1.25}>
                    <TextField
                        select
                        label="Channel"
                        size="small"
                        sx={{
                            minWidth: 120,
                            "& .MuiInputBase-root": {
                                fontSize: "11px",
                                minHeight: 34,
                                borderRadius: "8px",
                                background: "#fff",
                            },
                            "& .MuiInputLabel-root": { fontSize: "11px" },
                            "& .MuiSelect-select": {
                                display: "flex",
                                alignItems: "center",
                            },
                        }}
                        value={formData.channel}
                        onChange={handleChange("channel")}
                        SelectProps={{
                            MenuProps: compactSelectMenuProps,
                        }}
                    >
                        {channelOptions.map((item) => (
                            <MenuItem key={item.key} value={item.key}>
                                {item.name}
                            </MenuItem>
                        ))}
                    </TextField>
                </Box>
                <Box display="flex" columnGap={1.25}>
                    <TextField
                        select
                        label="Brand"
                        size="small"
                        sx={{
                            width: 190,
                            "& .MuiInputBase-root": {
                                fontSize: "11px",
                                minHeight: 34,
                                borderRadius: "8px",
                                background: "#fff",
                            },
                            "& .MuiInputLabel-root": { fontSize: "11px" },
                            "& .MuiSelect-select": {
                                display: "flex",
                                alignItems: "center",
                            },
                        }}
                        value={formData.brand}
                        onChange={handleChange("brand")}
                        SelectProps={{
                            multiple: true,
                            MenuProps: compactSelectMenuProps,
                            renderValue: (selected) => (
                                <Typography
                                    sx={{
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap",
                                        fontSize: "11px",
                                        width: "100%",
                                        display: "block",
                                    }}
                                >
                                    {brandOptions
                                    .filter((opt) => selected.includes(opt.key))
                                    .map((opt) => opt.name)
                                    .join(", ")}
                                </Typography>
                            ),
                        }}
                    >
                        {brandOptions.map((item) => (
                            <MenuItem key={item.key} value={item.key}>
                                <Checkbox
                                    checked={formData.brand.indexOf(item.key) > -1}
                                    size="small"
                                    sx={{ p: 0.5, mr: 0.5 }}
                                />
                                <ListItemText primary={item.name} />
                            </MenuItem>
                        ))}
                    </TextField>
                </Box>
                <Box display="flex"  alignItems="center" flex={1}>
                    <Button
                        variant="contained"
                        onClick={handleGetStoreData}
                        disabled={!hasSelectedChannelAndBrand}
                        sx={{ minHeight: 30, fontSize: "10px", px: 1.5, textTransform: "none", borderRadius: "7px" }}
                    >
                        Get Stores
                    </Button>
                </Box>
            </Box>
            <Box display="flex" columnGap={1.25} rowGap={1} mb="8px" justifyContent="space-between" flexWrap="wrap">
                <Box display="flex" columnGap={1.25} rowGap={1} alignItems="center" flexWrap="wrap" width="80%">
                    <Box
                        display="flex"
                        alignItems="center"
                        gap={1}
                        width={{ xs: "100%", md: "38%" }}
                        sx={{ border: "1px solid lightgray" }}
                        padding="4px 6px"
                        borderRadius="4px"
                    >
                        <Button
                            variant="outlined"
                            size="small"
                            component="label"
                            sx={{ height: 26, fontSize: "10px", textTransform: "none", minWidth: 90 }}
                            disabled={role || !hasSelectedChannelAndBrand}
                        >
                            Choose File
                            <input
                                type="file"
                                hidden
                                id = "fileInput"
                                accept=".csv,text/csv"
                                onChange={handleFileChange}
                                disabled={role || !hasSelectedChannelAndBrand}
                            />
                        </Button>

                        <Typography variant="caption" width="200px" noWrap sx={{ fontSize: "10px", color: "#667085" }}>
                            {file?.name || "No file chosen"}
                        </Typography>
                    </Box>
                    <Button
                        variant="contained"
                        disabled={role || !hasSelectedChannelAndBrand}
                        onClick={handleFileUpload}
                        sx={{ minHeight: 30, fontSize: "10px", px: 1.5, textTransform: "none", borderRadius: "7px" }}
                    >
                        Upload
                    </Button>
                    <Button
                        variant="contained"
                        disabled={role || !hasSelectedChannelAndBrand}
                        onClick={() => handleDownloadSample("sample")}
                        startIcon={<FileDownloadIcon />}
                        sx={{ minHeight: 30, fontSize: "10px", px: 1.5, textTransform: "none", borderRadius: "7px" }}
                    >
                        Sample
                    </Button>
                </Box>
                <Box>
                    <Button
                        variant="contained"
                        disabled={role || !hasSelectedChannelAndBrand}
                        onClick={() => handleDownloadSample("full")}
                        startIcon={<FileDownloadIcon />}
                        sx={{ minHeight: 30, fontSize: "10px", px: 1.5, textTransform: "none", borderRadius: "7px" }}
                    >
                        Download
                    </Button>
                </Box>
            </Box>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                <Typography fontWeight={700} sx={{ fontSize: "12px", color: "#17202a" }}>
                    Store Details
                </Typography>
                <TextField
                    size="small"
                    label="Search"
                    placeholder="Store name, grade..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    sx={{
                        minWidth: 280,
                        "& .MuiInputBase-root": { fontSize: "11px", minHeight: 34 },
                        "& .MuiInputLabel-root": { fontSize: "11px" },
                    }}
                />
            </Box>
            <Box
                width="100%"
                sx={{
                    height: "fit-content",
                    minHeight: 260,
                    maxHeight: "calc(100vh - 250px)",
                }}
            >
                <DataGrid
                    rows={filteredRows || []}
                    columns={storeDetailsColumns}
                    getRowId={(row) => row?._rowId}
                    rowHeight={36}
                    getRowHeight={() => "auto"}
                    columnHeaderHeight={36}
                    disableRowSelectionOnClick
                    pagination
                    paginationModel={paginationModel}
                    onPaginationModelChange={setPaginationModel}
                    pageSizeOptions={[5, 10, 25]}
                    localeText={{
                        noRowsLabel: storeRows?.length ? "No matching stores found" : "No store details found",
                    }}
                    autoHeight
                    sx={{
                        "& .MuiDataGrid-columnHeader": {
                            background: "#f8fafc",
                            fontWeight: 700,
                            fontSize: "10px",
                        },
                        "& .MuiDataGrid-columnHeaderTitle": {
                            fontSize: "10px",
                            fontWeight: 700,
                            color: "#344054",
                        },
                        "& .MuiDataGrid-cell": {
                            display: "flex",
                            alignItems: "center",
                            py: 0.75,
                            lineHeight: "normal",
                            fontSize: "10px",
                            color: "#344054",
                        },
                        "& .MuiDataGrid-footerContainer, & .MuiTablePagination-root": {
                            minHeight: 38,
                            fontSize: "10px",
                        },
                        "& .MuiTablePagination-toolbar": {
                            minHeight: 38,
                            alignItems: "center",
                        },
                        "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
                            fontSize: "10px",
                            marginBottom: 0,
                            marginTop: 0,
                        },
                        "& .MuiInputBase-root": {
                            fontSize: "10px",
                            alignItems: "center",
                        },
                        "& .MuiTablePagination-select": {
                            paddingTop: 0,
                            paddingBottom: 0,
                        },
                        "& .MuiTablePagination-actions": {
                            marginLeft: 1,
                        },
                        "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within": {
                            outline: "none",
                        },
                    }}
                />
            </Box>

        </Stack>
    );
}

export default DiscountStoreMaster;
